import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Clock,
  Sparkles,
  RefreshCw,
  AlertCircle,
  Plus,
  Trash2,
  ArrowRight,
  Check,
  Copy,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

interface PostRow {
  id: string;
  day_label: string;
  post_type: string;
  theme: string;
  current_time: string;
  caption_snippet: string;
  suggested_time?: string;
  reason?: string;
  applied?: boolean;
}

const POST_TYPES = ['Feed Post', 'Reel', 'Carousel'];

const blankRow = (i: number): PostRow => ({
  id: `row-${Date.now()}-${i}`,
  day_label: '',
  post_type: 'Feed Post',
  theme: '',
  current_time: '',
  caption_snippet: '',
});

const SAMPLE_ROWS: PostRow[] = [
  {
    id: 'sample-1',
    day_label: 'Monday',
    post_type: 'Reel',
    theme: 'Sunset over the harbor',
    current_time: '9:00 AM',
    caption_snippet: 'Golden hour reel showcasing the bay...',
  },
  {
    id: 'sample-2',
    day_label: 'Tuesday',
    post_type: 'Carousel',
    theme: 'Morning coffee ritual',
    current_time: '12:00 PM',
    caption_snippet: 'Seven steps to a slow, beautiful morning...',
  },
  {
    id: 'sample-3',
    day_label: 'Wednesday',
    post_type: 'Feed Post',
    theme: 'Local culture & craft',
    current_time: '6:00 PM',
    caption_snippet: 'Meet the artisans behind the work...',
  },
];

const ScheduleTool: React.FC = () => {
  const [rows, setRows] = useState<PostRow[]>(SAMPLE_ROWS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelUsed, setModelUsed] = useState<string>('');
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  const updateRow = (idx: number, patch: Partial<PostRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, blankRow(prev.length)]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    if (!confirm('Clear all rows?')) return;
    setRows([]);
    setHasAnalyzed(false);
  };

  const loadSample = () => {
    setRows(SAMPLE_ROWS);
    setHasAnalyzed(false);
  };

  const analyze = async () => {
    const valid = rows.filter((r) => r.day_label.trim() || r.theme.trim());
    if (valid.length === 0) {
      toast.error('Add at least one post with a day label or theme.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('auto-schedule', {
        body: {
          posts: valid.map((r) => ({
            id: r.id,
            day_label: r.day_label,
            date_label: '',
            theme: r.theme,
            post_type: r.post_type,
            current_time: r.current_time,
            caption_snippet: r.caption_snippet,
          })),
        },
      });
      if (invokeErr) throw new Error(invokeErr.message);
      const suggestions: { id: string; suggested_time: string; reason: string }[] =
        data?.suggestions || [];
      const byId = new Map(suggestions.map((s) => [s.id, s] as const));
      setRows((prev) =>
        prev.map((r) => {
          const s = byId.get(r.id);
          return s
            ? { ...r, suggested_time: s.suggested_time, reason: s.reason, applied: false }
            : r;
        }),
      );
      setModelUsed(data?.model_used || '');
      setHasAnalyzed(true);
      toast.success(`Analyzed ${suggestions.length} post${suggestions.length === 1 ? '' : 's'}`);
    } catch (e: any) {
      setError(e?.message || 'Analysis failed');
      toast.error('Analysis failed', { description: e?.message });
    } finally {
      setLoading(false);
    }
  };

  const applyOne = (idx: number) => {
    const r = rows[idx];
    if (!r.suggested_time) return;
    updateRow(idx, { current_time: r.suggested_time, applied: true });
    toast.success('Applied suggested time');
  };

  const applyAll = () => {
    const toApply = rows.filter((r) => r.suggested_time && !r.applied);
    if (toApply.length === 0) {
      toast.info('Nothing to apply');
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        r.suggested_time && !r.applied
          ? { ...r, current_time: r.suggested_time, applied: true }
          : r,
      ),
    );
    toast.success(`Applied ${toApply.length} suggested time${toApply.length === 1 ? '' : 's'}`);
  };

  const copyAsCSV = async () => {
    const header = 'Day,Post Type,Theme,Suggested Time,Reason';
    const lines = rows
      .filter((r) => r.suggested_time)
      .map(
        (r) =>
          `"${r.day_label}","${r.post_type}","${r.theme}","${r.suggested_time}","${(r.reason || '').replace(/"/g, "'")}"`,
      );
    const csv = [header, ...lines].join('\n');
    try {
      await navigator.clipboard.writeText(csv);
      toast.success('CSV copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const changedCount = rows.filter(
    (r) =>
      r.suggested_time &&
      (r.suggested_time || '').toLowerCase() !== (r.current_time || '').toLowerCase(),
  ).length;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-base text-[#1A3B5C]">Your weekly post list</h3>
          <p className="font-sans text-xs text-slate-500 mt-0.5">
            Add rows for each planned post. Click <em>Analyze</em> to get optimal times based on post
            type and theme.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={loadSample}
            className="font-sans text-[10px] tracking-wider uppercase px-3 py-2 border border-slate-200 text-slate-600 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
          >
            Load Sample
          </button>
          <button
            onClick={clearAll}
            className="font-sans text-[10px] tracking-wider uppercase px-3 py-2 border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={addRow}
            className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-wider uppercase px-3 py-2 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-colors"
          >
            <Plus className="w-3 h-3" /> Add Row
          </button>
          <button
            onClick={analyze}
            disabled={loading || rows.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] disabled:opacity-40 transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" /> Analyze with AI
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 font-sans text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold mb-1">Analysis failed</div>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500 w-[120px]">
                Day
              </th>
              <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500 w-[130px]">
                Type
              </th>
              <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">
                Theme
              </th>
              <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500 w-[110px]">
                Current
              </th>
              {hasAnalyzed && (
                <>
                  <th className="px-2 py-3 w-6"></th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-[#C9A961] w-[110px]">
                    Suggested
                  </th>
                  <th className="px-3 py-3 font-sans text-[9px] tracking-[0.2em] uppercase text-slate-500">
                    Reason
                  </th>
                  <th className="px-3 py-3 w-[80px]"></th>
                </>
              )}
              <th className="px-3 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={hasAnalyzed ? 9 : 6} className="px-3 py-12 text-center font-sans text-sm text-slate-400">
                  No rows yet. Click <em>Add Row</em> or <em>Load Sample</em> to get started.
                </td>
              </tr>
            )}
            {rows.map((r, idx) => {
              const changed =
                !!r.suggested_time &&
                (r.suggested_time || '').toLowerCase() !== (r.current_time || '').toLowerCase();
              return (
                <tr
                  key={r.id}
                  className={`border-b border-slate-100 ${
                    r.applied ? 'bg-emerald-50/40' : ''
                  }`}
                >
                  <td className="px-3 py-2 align-top">
                    <input
                      value={r.day_label}
                      onChange={(e) => updateRow(idx, { day_label: e.target.value })}
                      placeholder="Monday"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <select
                      value={r.post_type}
                      onChange={(e) => updateRow(idx, { post_type: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none"
                    >
                      {POST_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      value={r.theme}
                      onChange={(e) => updateRow(idx, { theme: e.target.value })}
                      placeholder="Sunset over the harbor"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      value={r.current_time}
                      onChange={(e) => updateRow(idx, { current_time: e.target.value })}
                      placeholder="9:00 AM"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none font-mono"
                    />
                  </td>
                  {hasAnalyzed && (
                    <>
                      <td className="px-2 py-2 align-middle text-center">
                        {changed ? (
                          <ArrowRight className="w-3.5 h-3.5 text-[#C9A961] mx-auto" />
                        ) : r.suggested_time ? (
                          <span className="text-slate-300">·</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {r.suggested_time ? (
                          <span
                            className={`inline-block font-mono text-xs font-semibold px-2 py-1 rounded ${
                              changed
                                ? 'bg-[#C9A961]/15 text-[#8B6F47] border border-[#C9A961]/40'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            {r.suggested_time}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <p className="font-sans text-[11px] text-slate-600 leading-snug max-w-[280px]">
                          {r.reason || <span className="italic text-slate-400">—</span>}
                        </p>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {r.suggested_time && changed && !r.applied && (
                          <button
                            onClick={() => applyOne(idx)}
                            className="px-2 py-1 font-sans text-[10px] tracking-wider uppercase border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-colors"
                          >
                            Apply
                          </button>
                        )}
                        {r.applied && (
                          <span className="inline-flex items-center gap-1 font-sans text-[10px] tracking-wider uppercase text-emerald-600">
                            <Check className="w-3 h-3" /> Done
                          </span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 align-top">
                    <button
                      onClick={() => removeRow(idx)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      title="Remove row"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Results footer */}
      {hasAnalyzed && rows.length > 0 && (
        <div className="bg-[#F5F1E8] border border-[#C9A961]/30 p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="font-sans text-xs text-[#8B6F47]">
            <span className="font-semibold">{changedCount}</span> suggested change
            {changedCount === 1 ? '' : 's'} ready to apply
            {modelUsed && (
              <span className="ml-2 text-slate-500">
                · model: <span className="font-mono">{modelUsed}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyAsCSV}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-600 font-sans text-[10px] tracking-[0.2em] uppercase hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
            >
              <Copy className="w-3 h-3" /> Export CSV
            </button>
            <button
              onClick={applyAll}
              disabled={changedCount === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] disabled:opacity-40 transition-all"
            >
              <Check className="w-3.5 h-3.5" /> Apply All Suggested Times
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleTool;
